import { Component, OnInit, ViewChild } from '@angular/core';
import { BaseComponent } from '@core/components/base-component';
import { DepartmentPickerItem } from '@data-managment/department-picker/shared/department-picker-item';
import { ProjectPickerItem } from '@data-managment/project-picker/models/project-picker.item';
import { ImportProjectDto } from '@interfaces/ImportProjectDTO';
import { ImportProjectRequestDto } from '@interfaces/ImportProjectRequestDTO';
import { EstimateSettingsService } from '@shared/services/estimate-settings.service';
import { LoadingService } from '@shared/services/loading.service';
import { DepartmentPickerService } from '../department-picker/shared/department-picker.service';
import { ProjectPickerComponent } from '../project-picker/project-picker.component';
import { EstimateImportService } from './shared/estimate-import.service';

@Component({
  selector: 'pop-project-import',
  templateUrl: './project-import.component.html',
  styleUrls: ['./project-import.component.less']
})
export class ProjectImportComponent extends BaseComponent implements OnInit {
  @ViewChild('projectPicker', { static: true })
  private projectPicker?: ProjectPickerComponent;

  public resultsList: {
    titleKey: string;
    routerLink: string[];
    referenceCode: string;
  }[] = [];

  public selectedProject?: ProjectPickerItem;

  private readonly showAllProjects_key = 'ProjectImportComponent.showAllProjects';
  private _showAllProjects: boolean = sessionStorage.getItem(this.showAllProjects_key) === 'true';
  public get showAllProjects() {
    return this._showAllProjects;
  }
  public set showAllProjects(value: boolean) {
    this._showAllProjects = value;
    sessionStorage.setItem(this.showAllProjects_key, value + '');
    this.updateFilter();
  }

  public canImportProject: boolean;
  public canExportProject: boolean;

  public selectedDepartment: DepartmentPickerItem;

  public referenceCode: string;
  public referenceCodeFallback: string;

  public constructor(
    private estimateImportService: EstimateImportService,
    private estimateSettingsService: EstimateSettingsService,
    private departmentPickerService: DepartmentPickerService,
    private loadingService: LoadingService
  ) {
    super();
  }

  public async ngOnInit() {
    this.loadingService.setIsLoading(true);
    const departments = await this.departmentPickerService.getDepartments();

    this.subscribe(
      this.estimateSettingsService.getEstimateConfiguration(false).subscribe(config => {
        if (!this.selectedDepartment) {
          // only update selected department if its null
          this.selectedDepartment = departments.find(s => s.code === config.defaultImportDepartmentCode);
        }
        this.loadingService.setIsLoading(false);
      })
    );
  }

  public onSelectedProjectChanged() {
    this.updateReferenceCode();

    this.canImportProject = this.selectedProject && !this.selectedProject?.isImported;
    if (this.showAllProjects && this.selectedProject) {
      this.canImportProject = true; // you can only re-import if the flag is set
    }

    this.canExportProject = this.selectedProject?.isImported;
  }

  public toggleShowAllProjects() {
    this.selectedProject = undefined;
    this.canImportProject = false;
  }

  private updateReferenceCode() {
    if (this.selectedProject?.estimateCodeRef) {
      this.referenceCodeFallback = this.selectedProject?.estimateCodeRef;
      return;
    }

    const projectCode = this.selectedProject?.project.code;
    if (projectCode) {
      this.referenceCodeFallback = `${projectCode} [i]`;
      return;
    }
    this.referenceCodeFallback = this.referenceCode = '';
  }

  private getReferenceCode() {
    const referenceCode = this.referenceCode || this.referenceCodeFallback;
    return referenceCode;
  }

  public updateFilter() {
    this.projectPicker?.filter();
  }

  public filterProjects = ((dto: ImportProjectDto) => {
    if (this.showAllProjects) {
      return true;
    }
    return !dto.isImported && !dto.hasPCSyncBeenRequested;
  }).bind(this); // this is passed by reference. Bind is used to maintain the value of the keyword this

  public async importProject() {
    const referenceCode = this.getReferenceCode();

    const importDto: ImportProjectRequestDto = {
      departmentCode: this.selectedDepartment?.code,
      projectCode: this.selectedProject.project.code,
      refCode: referenceCode
    };

    const estimateId = await this.estimateImportService.import(importDto);

    this.resultsList.push({
      titleKey: 'DATA_MANAGEMENT.IMPORT_SUCCESS',
      routerLink: ['/app/', 'estimate-detail', estimateId],
      referenceCode: referenceCode
    });

    this.selectedProject = undefined;
    this.estimateImportService.refreshGetProjectsForImport();
  }
}
